import { zzfx, ZZFX } from "zzfx";

// Procedural retro SFX (ZzFX). The shared AudioContext is created at import but
// stays suspended until initAudio() runs on the first user gesture (Start click),
// satisfying browser autoplay rules. A global mute flag silences everything.

let muted = false;
let started = false;

export function initAudio() {
  started = true;
  try {
    void ZZFX.audioContext.resume();
  } catch {
    /* ignore */
  }
}

export function isMuted() {
  return muted;
}

export function toggleMute() {
  muted = !muted;
  return muted;
}

function play(params: Array<number | undefined>) {
  if (muted || !started) return;
  try {
    zzfx(...params);
  } catch {
    /* audio not ready */
  }
}

// Sound definitions (ZzFX parameter arrays; sparse holes use ZzFX defaults).
// Reload groan stretched to ~1.35s to span the reload (reload_time ~1.4s).
const RELOAD_SND: Array<number | undefined> = [0.7, 0.2, 60, 0.05, 0.7, 0.6, , 0.4, -2, , , , , 0.3, , 0.2, , 0.6];

export const sfx = {
  shoot: () => play([2, 0.1, 90, 0.01, 0.09, 0.3, 4, 1.8, , , , , , 2, , 0.4, , 0.6, 0.05]),
  // last shell in the mag: normal blast + a loud, ringing high "ting" after it
  // decays, so the empty cue cuts through the blast noise.
  shootLast: () => {
    sfx.shoot();
    setTimeout(() => play([2, 0, 2600, 0.005, 0.06, 0.18, 1, , , , , , , , , , , 0.7]), 220);
  },
  // out-of-ammo: loud double "click-click" (lower then higher)
  dryFire: () => {
    play([1.6, 0, 480, , 0.004, 0.03, 2, 1, , , , , , , , , , 0.4]);
    setTimeout(() => play([1.6, 0, 1100, , 0.004, 0.03, 2, 1, , , , , , , , , , 0.4]), 75);
  },
  // reload uses the (stretched) groan sound; zombie appearance is silent.
  // `scale` (0..1) shortens the attack/sustain/release to match a partial reload.
  reload: (scale = 1) => {
    const p = RELOAD_SND.slice();
    for (const i of [3, 4, 5]) p[i] = (p[i] as number) * scale; // attack, sustain, release
    play(p);
  },
  splat: () => play([1.1, 0.1, 120, 0.01, 0.06, 0.16, 4, 1.4, , , , , , 1.6, , 0.4, , 0.5]),
  // bonus jingle (rising notes) for the bat pickup / survival bonus
  bonus: () => {
    const notes = [523, 659, 784, 1047];
    notes.forEach((f, i) => setTimeout(() => play([0.85, 0, f, 0.01, 0.09, 0.18, 1, , , , , , , , , , , 0.6]), i * 70));
  },
  // killer zombie rearing up to lunge: a low, raspy, wavering growl
  growl: () => play([2.3, 0.05, 32, 0.03, 0.4, 0.45, 2, 1.5, -2, , , , , 0.4, 5, 0.2, , 0.7, 0.2, 0.3]),
  // boss heaving up out of the ground: a deeper, longer, more monstrous roar
  bossRoar: () => play([3, 0.08, 22, 0.06, 0.9, 0.7, 2, 1.6, -1, , , , , 0.5, 6, 0.3, 0.1, 0.7, 0.4, 0.25]),
  // boss lobs an axe: an airy descending whoosh (warns it's incoming)
  axeThrow: () => play([1.1, 0.1, 360, 0.02, 0.1, 0.14, 4, 0.6, -9, , , , , 1.2, , 0.2, , 0.6, 0.05]),
  // shooting an axe out of the air: a short metallic clang
  axeBreak: () => play([1.5, 0.05, 1400, 0, 0.03, 0.12, 1, 1.4, , , , , , 0.4, , 0.3, 0.04, 0.5, 0.06]),
  gameOver: () => {
    // longer descending tune
    const notes = [466, 415, 370, 311, 277, 233, 196, 165, 139, 110, 87];
    notes.forEach((f, i) => setTimeout(() => play([0.9, 0, f, 0.02, 0.2, 0.34, 1, , , , , , , , , , , 0.6]), i * 180));
  },
};

// ---------- Bat flutter ----------
// A rapid, high "chee-chee" squeak that flips between high C and G on every
// wing half-stroke, so the sound tracks the flapping. The wings flap at
// speed=30 rad/s (see entities/bat.ts) → a full beat every ~0.21s, so a chirp
// every ~105ms lands one note per up/down stroke. Returns a stop() handle the
// bat calls when it dies or flies off; `active` gates chirps while paused, and
// play() already honours the global mute flag.
const BAT_C = 1047; // C6 squeak (high but reliably audible)
const BAT_G = 1568; // G6 squeak
const BAT_HALFSTROKE_MS = 105;

export function startBatFlutter(active: () => boolean): () => void {
  let i = 0;
  const tick = () => {
    if (!active()) return;
    const f = i++ % 2 === 0 ? BAT_C : BAT_G;
    // a present "chee" squeak: longer + mostly tonal so it's clearly audible
    play([0.7, 0.15, f, 0.01, 0.05, 0.09, 1, 1.4, , , , , , 0.4, , , , 0.6, , 0.12]);
  };
  tick();
  const id = setInterval(tick, BAT_HALFSTROKE_MS);
  return () => clearInterval(id);
}

// ---------- Boss dig ----------
// A rapid, gritty low "tch-tch-tch" earth-scratch played while the boss claws
// its way up out of the ground (the rise phase). Same ticking pattern as the
// bat flutter, but low and noisy (digging dirt) instead of a high squeak.
// Returns a stop() handle the boss calls once it has fully emerged.
const DIG_LO = 70;
const DIG_HI = 108;
const DIG_TICK_MS = 115;

export function startBossDig(active: () => boolean): () => void {
  let i = 0;
  const tick = () => {
    if (!active()) return;
    const f = i++ % 2 === 0 ? DIG_LO : DIG_HI;
    // short, dirty noise burst — a clawed scrape through soil
    play([1.1, 0.25, f, 0, 0.02, 0.05, 4, 1.4, , , , , , 2, , 0.4, , 0.4, , 0.15]);
  };
  tick();
  const id = setInterval(tick, DIG_TICK_MS);
  return () => clearInterval(id);
}

// ---------- Menu music ----------
// "House of the Rising Sun", recorded in the in-house piano composer (games.datapro.cz/piano):
// two takes of the same line — a flowing legato verse and a plucked staccato one — looped on
// the title screen with a long pause between them. Onsets are kept as performed (human feel);
// the legato take is smoothed so notes connect. Honours the global mute flag.
const SEMITONE: Record<string, number> = { C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5, "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11 };
function noteFreq(name: string): number {
  const m = /^([A-G]#?)(\d)$/.exec(name)!;
  return 440 * Math.pow(2, (SEMITONE[m[1]] + (parseInt(m[2], 10) + 1) * 12 - 69) / 12);
}

// [note, onsetSec] as performed.
const LEGATO_RAW: Array<[string, number]> = [
  ["F4", 1.22], ["G4", 1.955], ["G#4", 2.373], ["C5", 3.144], ["B4", 3.608], ["B4", 4.351], ["A#4", 4.805],
  ["A#4", 5.564], ["F4", 5.999], ["F4", 6.798], ["E4", 7.233], ["E4", 7.988], ["F4", 8.457], ["C4", 9.767],
  ["F4", 11.093], ["G4", 11.88], ["G#4", 12.426], ["D5", 13.332], ["C#5", 13.785], ["C#5", 14.518], ["C5", 14.937],
  ["C5", 15.691], ["F4", 16.095], ["F4", 16.857], ["E4", 17.247], ["F4", 17.628], ["G4", 17.976], ["F4", 18.378],
];
const STACCATO_RAW: Array<[string, number]> = [
  ["F4", 0.761], ["G4", 1.397], ["G#4", 1.687], ["C5", 2.302], ["B4", 2.634], ["B4", 3.216], ["A#4", 3.502],
  ["A#4", 4.102], ["F4", 4.387], ["F4", 5.002], ["E4", 5.272], ["E4", 5.908], ["F4", 6.209], ["F4", 6.853],
  ["C4", 7.179], ["F4", 8.146], ["G4", 8.764], ["G#4", 9.1], ["D5", 9.699], ["C#5", 10.031], ["C#5", 10.632],
  ["C5", 10.955], ["C5", 11.589], ["F4", 11.923], ["F4", 12.556], ["E4", 12.858], ["F4", 13.16], ["G4", 13.494], ["F4", 13.797],
];

interface Tone { f: number; t: number; d: number; pluck: boolean; }

// Legato: each note holds up to the next onset so the line connects (final note rings out).
function buildLegato(raw: Array<[string, number]>): Tone[] {
  const t0 = raw[0][1];
  return raw.map(([n, t], i) => {
    const start = t - t0;
    const next = i + 1 < raw.length ? raw[i + 1][1] - t0 : start + 1.6;
    return { f: noteFreq(n), t: start, d: Math.min(2, (next - start) * 0.97), pluck: false };
  });
}
// Staccato: short plucks at the performed onsets.
function buildStaccato(raw: Array<[string, number]>): Tone[] {
  const t0 = raw[0][1];
  return raw.map(([n, t]) => ({ f: noteFreq(n), t: t - t0, d: 0.14, pluck: true }));
}

const SEGMENTS: Tone[][] = [buildLegato(LEGATO_RAW), buildStaccato(STACCATO_RAW)];
const SEG_LEN = SEGMENTS.map((s) => Math.max(...s.map((n) => n.t + n.d)));
const MENU_LEAD = 0.5; // lead-in before the first note
const MENU_GAP_BETWEEN = 10; // silence between the two takes
const MENU_GAP_LOOP = 120; // silence after both takes before the whole thing repeats

let menuTimers: ReturnType<typeof setTimeout>[] = [];
let menuOn = false;

function playTone(t: Tone) {
  if (t.pluck) {
    // short decay (last param) ramps off the attack peak so the pluck doesn't click either
    play([0.4, 0, t.f, 0.008, Math.max(0.02, t.d * 0.4), 0.1, 1, , , , , , , , , , , 0.4, Math.max(0.03, t.d * 0.5)]);
  } else {
    // gentle swell: longer attack (scaled to the note) so it eases in, not a steep snap.
    // A real decay (last param) ramps the attack peak down to the sustain level instead of
    // stepping to it — that instant 1.0->0.6 jump was the click on every note.
    const attack = Math.min(0.16, Math.max(0.07, t.d * 0.35));
    const decay = Math.max(0.06, t.d * 0.25);
    play([0.42, 0, t.f, attack, Math.max(0.04, t.d * 0.3), Math.max(0.16, t.d * 0.6), 1, , , , , , , , , , , 0.6, decay]);
  }
}

function menuLoop() {
  if (!menuOn) return;
  menuTimers = [];
  let cursor = MENU_LEAD;
  SEGMENTS.forEach((seg, i) => {
    for (const n of seg) menuTimers.push(setTimeout(() => playTone(n), (cursor + n.t) * 1000));
    cursor += SEG_LEN[i] + (i < SEGMENTS.length - 1 ? MENU_GAP_BETWEEN : MENU_GAP_LOOP);
  });
  menuTimers.push(setTimeout(menuLoop, cursor * 1000));
}

// Starts only once the audio context is unlocked (started === true); otherwise no-op,
// so call it again on the first user gesture. Safe to call repeatedly.
export function startMenuMusic() {
  if (menuOn || !started) return;
  menuOn = true;
  menuLoop();
}
export function stopMenuMusic() {
  menuOn = false;
  for (const t of menuTimers) clearTimeout(t);
  menuTimers = [];
}
